import { useQuery } from "@apollo/client"
import {
  ArrowDropDown,
  ArrowRight,
  AssessmentOutlined,
  PeopleOutline,
  RunCircleOutlined,
  ViewAgendaOutlined,
} from "@mui/icons-material"
import { alpha, Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, styled } from "@mui/material"
import { useState } from "react"
import { useSelector } from "react-redux"
import { Link, useLocation } from "react-router-dom"
import { profileSelector } from "reducers/profileSlice"
import { privateRoute } from "routes"
import { EMPLOYEE_ACCESS } from "views/Employees/graphql"
import { canViewRequestStatistics } from "views/RequestStatistics/utils"

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
  ".MuiListItemIcon-root": {
    marginRight: theme.spacing(1),
    minWidth: 0,
  },
  ".MuiListItemText-primary": {
    fontWeight: theme.typography.fontWeightBold,
  },
  "&.MuiListItemButton-root.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
  },
  "&.MuiListItemButton-root.MuiButtonBase-root:hover": {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
  },
  borderRadius: theme.shape.borderRadius * 2,
}))

type SubMenuType = {
  name?: JSX.Element | string
  path: string
}

type AppMenuOrientation = "horizontal" | "vertical"

type MenuItemProps = {
  icon?: JSX.Element
  items?: SubMenuType[]
  name?: JSX.Element | string
  orientation?: AppMenuOrientation
  path: string
}

const MenuItem = ({ icon, items, name, orientation = "vertical", path }: MenuItemProps) => {
  const location = useLocation()
  const [open, setOpen] = useState(location.pathname.startsWith(path))

  const isHome = path === "/"
  const isContain = location.pathname.startsWith(path)
  const isSelected = isHome ? location.pathname === path : isContain

  return (
    <Box flexShrink={0}>
      {items ? (
        <StyledListItem onClick={() => setOpen((current) => !current)} selected={isContain}>
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText>{name}</ListItemText>
          {!open ? <ArrowRight /> : <ArrowDropDown />}
        </StyledListItem>
      ) : (
        <Link to={path}>
          <StyledListItem selected={isSelected}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>{name}</ListItemText>
          </StyledListItem>
        </Link>
      )}

      {items && (
        <Collapse in={open}>
          <List
            disablePadding
            sx={(theme) => ({
              ".Mui-selected > .MuiListItemIcon-root:before": {
                opacity: 1,
              },
              ".MuiListItemButton-root": {
                px: 2,
                py: 0.75,
              },
              ".MuiListItemIcon-root:before": {
                backgroundColor: "primary.main",
                borderRadius: 1,
                content: "''",
                height: theme.spacing(1),
                opacity: theme.palette.action.disabledOpacity,
                width: theme.spacing(1),
              },
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pl: 2,
              pt: 1,
            })}
          >
            {items?.map((sub) => (
              <MenuItem key={sub.path} orientation={orientation} {...sub} />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  )
}

type Props = {
  orientation?: AppMenuOrientation
}

const AppMenu = ({ orientation = "vertical" }: Props) => {
  const { user } = useSelector(profileSelector)
  const { data: employeeAccess } = useQuery(EMPLOYEE_ACCESS)

  return (
    <List
      disablePadding
      sx={{
        display: "flex",
        flexDirection: orientation === "horizontal" ? "row" : "column",
        gap: 1,
      }}
    >
      {employeeAccess?.canViewEmployees && (
        <MenuItem {...privateRoute.employees} icon={<PeopleOutline />} orientation={orientation} />
      )}
      <MenuItem {...privateRoute.works} icon={<ViewAgendaOutlined />} orientation={orientation} />
      <MenuItem {...privateRoute.requestLeave} icon={<RunCircleOutlined />} orientation={orientation} />
      {canViewRequestStatistics(user?.position) && (
        <MenuItem {...privateRoute.requestStatistics} icon={<AssessmentOutlined />} orientation={orientation} />
      )}
    </List>
  )
}

export default AppMenu
